// src/pages/PendingConfirmationPage.js
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getAgreementByAgreementID, cancelAgreement } from '../firebase/services';
import { buildShareMessage, buildWhatsAppURL } from '../utils/payment';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../hooks/useAuth';
import Navbar from '../components/Navbar';
import CircleIcon, { SvgIcon } from '../components/Icon';
import Lightbox from '../components/Lightbox';

export default function PendingConfirmationPage() {
  const { agreementID } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [participantsExpanded, setParticipantsExpanded] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState(null);

  // Initial fetch to get the Firestore doc ID
  useEffect(() => {
    getAgreementByAgreementID(agreementID).then(a => {
      setAgreement(a);
      setLoading(false);

      // Set up real-time listener if it's a group agreement
      if (a?.id && a.participants?.length > 0) {
        const unsub = onSnapshot(doc(db, 'agreements', a.id), (snap) => {
          if (snap.exists()) {
            setAgreement(prev => ({ ...prev, ...snap.data() }));
          }
        });
        return () => unsub();
      }
    });
  }, [agreementID]);

  if (loading) return <div className="page"><Navbar /><div className="container" style={{ paddingTop: 32 }}>{t('pendingPage.loading')}</div></div>;
  if (!agreement) return <div className="page"><Navbar /><div className="container" style={{ paddingTop: 32 }}>{t('pendingPage.notFound')}</div></div>;

  const baseURL = window.Capacitor?.isNativePlatform?.() ? 'https://www.tuttoclaro.com' : window.location.origin;
  const message = buildShareMessage(agreement.title, agreementID, baseURL);
  const isGroup = agreement.participants && agreement.participants.length > 0;

  const handleResend = async () => {
    setResending(true);
    setResendMsg(null);
    try {
      const fns = getFunctions();
      const sendVerify = httpsCallable(fns, 'sendVerificationInvite');
      await sendVerify({ agreementId: agreementID, lang: i18n.language?.startsWith('es') ? 'es' : 'en' });
      setResendMsg({ type: 'success', text: t('pendingPage.resendSuccess') });
    } catch (err) {
      const msg = err?.message?.includes('rate') || err?.message?.includes('wait') || err?.code === 'functions/resource-exhausted'
        ? t('pendingPage.resendTooSoon')
        : t('pendingPage.resendFailed');
      setResendMsg({ type: 'error', text: msg });
    } finally {
      setResending(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(message);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const confirmedCount = isGroup ? agreement.participants.filter(p => p.status === 'Confirmed').length : 0;
  const totalCount = isGroup ? agreement.participants.length : 0;

  return (
    <div className="page">
      <Navbar />
      <div className="container" style={{ paddingTop: 28, paddingBottom: 60 }}>

        <div className="hero hero-pending">
          <div className="hero-icon" style={{ display: 'flex', justifyContent: 'center' }}><CircleIcon name="clock" color="rgba(255,255,255,0.25)" size={52} iconSize={26} /></div>
          <div className="hero-title">{t('pendingPage.waitingTitle')}</div>
          <div className="hero-sub">{t('pendingPage.waitingSub', { name: agreement.partnerName || 'all participants' })}</div>
        </div>

        <div className="card">
          <div className="detail-row">
            <div className="detail-label">{t('pendingPage.agreement')}</div>
            <div className="detail-value" style={{ fontFamily: 'Noto Sans, sans-serif', fontWeight: 700 }}>
              {agreement.title}
              {agreement.evidence?.length > 0 && <span className="evidence-badge"><SvgIcon name="paperclip" size={12} color="var(--text)" /> {agreement.evidence.length}</span>}
            </div>
          </div>
          <div className="detail-row">
            <div className="detail-label">{t('pendingPage.between')}</div>
            <div className="detail-value">{agreement.createdByName} & {agreement.partnerName || 'group'}</div>
          </div>
          {agreement.amount && (
            <div className="detail-row">
              <div className="detail-label">{t('pendingPage.amount')}</div>
              <div className="detail-value" style={{ fontFamily: 'Noto Sans, sans-serif', fontWeight: 800, color: 'var(--primary)' }}>
                {agreement.currency} {agreement.amount}
              </div>
            </div>
          )}
          <div className="detail-row">
            <div className="detail-label">{t('pendingPage.details')}</div>
            <div className="detail-value" style={{ color: 'var(--muted)', fontSize: '0.84rem' }}>{agreement.details}</div>
          </div>
          {agreement.deadline && (
            <div className="detail-row">
              <div className="detail-label">{t('pendingPage.deadline')}</div>
              <div className="detail-value">{new Date(agreement.deadline).toLocaleString()}</div>
            </div>
          )}
          {agreement.appointmentDate && (
            <div className="detail-row">
              <div className="detail-label">{t('pendingPage.appointmentDate')}</div>
              <div className="detail-value">{new Date(agreement.appointmentDate).toLocaleString()}</div>
            </div>
          )}
          {agreement.evidence?.length > 0 && (
            <div className="detail-row">
              <div className="detail-label"><SvgIcon name="paperclip" size={13} color="var(--text)" /> {t('pendingPage.evidence', { count: agreement.evidence.length })}</div>
              <div className="evidence-view-grid" style={{ marginTop: 6 }}>
                {agreement.evidence.map((ev, i) =>
                  ev.type?.includes('pdf') ? (
                    <a key={i} href={ev.url} target="_blank" rel="noreferrer" className="evidence-view-thumb" title={ev.fileName || 'View file'}>
                      <SvgIcon name="doc" size={18} color="var(--text)" />
                    </a>
                  ) : (
                    <div key={i} className="evidence-view-thumb" style={{ cursor: 'pointer' }} onClick={() => setLightboxSrc(ev.url)} title={ev.fileName || 'View image'}>
                      <img src={ev.url} alt="evidence" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }} />
                    </div>
                  )
                )}
              </div>
              <div style={{ marginTop: 6 }}>
                {agreement.evidence.map((ev, i) => (
                  <a key={i} href={ev.url} target="_blank" rel="noreferrer" download style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.72rem', color: 'var(--primary)', marginRight: 12, marginBottom: 4 }}>
                    <SvgIcon name="download" size={12} color="var(--primary)" /> {ev.fileName || `File ${i + 1}`}
                  </a>
                ))}
              </div>
            </div>
          )}
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 8, fontSize: '0.72rem', color: 'var(--muted)' }}>
            {t('pendingPage.agreementID')} <strong>{agreementID}</strong>
          </div>
        </div>

        {agreement.verified ? (
          <div className="card" style={{ background: 'var(--confirmed-bg)', border: '1px solid var(--confirmed)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <SvgIcon name="shield" size={16} color="var(--confirmed)" strokeWidth={2}/>
              <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--confirmed)' }}>
                {t('pendingPage.verifiedBadge', { email: agreement.participantEmail })}
              </span>
            </div>
            <button
              className="btn btn-secondary btn-sm"
              style={{ fontSize: '0.76rem', marginBottom: 0 }}
              disabled={resending}
              onClick={handleResend}
            >
              <SvgIcon name="send" size={13} color="var(--text)" strokeWidth={2}/> {resending ? t('pendingPage.resending') : t('pendingPage.resendEmail')}
            </button>
            {resendMsg && <p style={{ fontSize: '0.74rem', color: resendMsg.type === 'error' ? 'var(--declined)' : 'var(--confirmed)', marginTop: 6, marginBottom: 0 }}>{resendMsg.text}</p>}
          </div>
        ) : (
          <div className="share-card">
            <div className="share-title">{t('pendingPage.shareWith', { name: agreement.partnerName || 'participants' })}</div>
            <button className="btn btn-primary" onClick={handleCopy}>
              {copied ? <><SvgIcon name="check" size={14} color="white" /> {t('pendingPage.copied')}</> : <><SvgIcon name="doc" size={14} color="white" /> {t('pendingPage.copyLink')}</>}
            </button>
            <a className="btn btn-secondary" href={buildWhatsAppURL(message)} target="_blank" rel="noreferrer">
              <SvgIcon name="chat" size={14} color="var(--text)" /> {t('pendingPage.shareWhatsApp')}
            </a>
          </div>
        )}


        {/* Group participant status — collapsible */}
        {isGroup && (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div
              onClick={() => setParticipantsExpanded(!participantsExpanded)}
              style={{
                padding: '14px 16px', cursor: 'pointer', userSelect: 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                borderBottom: participantsExpanded ? '1px solid var(--border)' : 'none',
              }}
            >
              <div>
                <div style={{ fontWeight: 700, fontSize: '0.86rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <SvgIcon name="users" size={15} color="var(--text)" /> {t('pendingPage.participantStatus')}
                </div>
                <div style={{ fontSize: '0.74rem', color: 'var(--muted)', marginTop: 2 }}>
                  {t('pendingPage.xOfYConfirmed', { confirmed: confirmedCount, total: totalCount })}
                </div>
              </div>
              <span style={{
                fontSize: '0.9rem', color: 'var(--muted)',
                transition: 'transform 0.2s',
                transform: participantsExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
              }}>▼</span>
            </div>
            {participantsExpanded && (
              <div style={{ padding: '8px 16px 14px' }}>
                {agreement.participants.map((p, i) => {
                  const badgeClass = p.status === 'Confirmed' ? 'badge-confirmed'
                    : p.status === 'Declined' ? 'badge-declined'
                    : 'badge-pending';
                  return (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '10px 0',
                      borderBottom: i < agreement.participants.length - 1 ? '1px solid var(--border)' : 'none',
                    }}>
                      <span style={{ fontSize: '0.86rem', fontWeight: 500 }}>{p.name}</span>
                      <span className={`badge ${badgeClass}`}>{p.status || 'Pending'}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>
          {t('pendingPage.backToDashboard')}
        </button>

        {user?.uid === agreement.createdByUID && (
          <button
            className="btn btn-danger"
            style={{ marginTop: 4 }}
            disabled={cancelling}
            onClick={async () => {
              if (!window.confirm(t('pendingPage.cancelConfirm'))) return;
              setCancelling(true);
              try {
                await cancelAgreement(agreement.id);
                navigate('/dashboard');
              } catch (err) {
                console.error('Cancel error:', err);
                setCancelling(false);
              }
            }}
          >
            <SvgIcon name="x" size={14} color="var(--declined)" /> {cancelling ? t('common.saving') : t('pendingPage.cancelAgreement')}
          </button>
        )}
      </div>
      {lightboxSrc && <Lightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}
    </div>
  );
}
